import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { DateFormat, DisplayMode } from '@treneo/shared/constants';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_TIME_FORMAT,
  DEFAULT_TIME_STEP,
  TimeFormat
} from '@treneo/shared/constants';

import type { TagColorMode, TagVariant } from '../tags-config';
import { DEFAULT_BORDER_SIZE, DEFAULT_FONT_SIZE, DEFAULT_UNIFORM_COLOR } from '../tags-config';

import {
  getDefaultConfigUiState,
  loadDateTimePickerSettingsFromStorage,
  loadTagsSettingsFromStorage,
  validateColorValue,
  validateNumberInRange
} from './config-ui-persistence';
import type {
  AppConfigState,
  DateTimePickerSettings,
  PaginationSettings,
  TagsSettings,
  TagStylingSettings
} from './types';

const MIN_PAGE_SIZE = 1;
const MIN_BORDER_SIZE = 1;
const MAX_BORDER_SIZE = 10;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 24;

const defaultTagStyling = (): TagStylingSettings => ({
  uniformColor: DEFAULT_UNIFORM_COLOR,
  borderSize: DEFAULT_BORDER_SIZE,
  fontSize: DEFAULT_FONT_SIZE
});

const defaultDateTimeSettings = (): DateTimePickerSettings => ({
  dateFormat: DEFAULT_DATE_FORMAT,
  timeFormat: DEFAULT_TIME_FORMAT,
  displayMode: DEFAULT_DISPLAY_MODE,
  timeStep: DEFAULT_TIME_STEP,
  mondayFirst: true
});

type ConfigUiActions = {
  setListPaginationPageSize: (payload: {
    listName: keyof PaginationSettings['lists'];
    pageSize: number;
  }) => void;
  setTagsSettings: (payload: Partial<TagsSettings>) => void;
  setTagVariant: (payload: TagVariant) => void;
  setTagColorMode: (payload: TagColorMode) => void;
  setTagStyling: (payload: Partial<TagStylingSettings>) => void;
  setTagUniformColor: (payload: string) => void;
  setTagBorderSize: (payload: number) => void;
  setTagFontSize: (payload: number) => void;
  resetTagsSettings: () => void;
  setDateTimePickerSettings: (payload: Partial<DateTimePickerSettings>) => void;
  setDateTimeDateFormat: (payload: DateFormat) => void;
  setDateTimeTimeFormat: (payload: TimeFormat) => void;
  setDateTimeDisplayMode: (payload: DisplayMode) => void;
  setDateTimeTimeStep: (payload: number) => void;
  setDateTimeUse24HourFormat: (payload: boolean) => void;
  setDateTimeMondayFirst: (payload: boolean) => void;
  resetDateTimePickerSettings: () => void;
};

export type ConfigUiStore = AppConfigState & ConfigUiActions;

export const useConfigUiStore = create<ConfigUiStore>()(
  persist(
    (set, get) => ({
      ...getDefaultConfigUiState(),

      setListPaginationPageSize: ({ listName, pageSize }) => {
        if (!listName || typeof pageSize !== 'number') {
          return;
        }
        const listSettings = get().paginationSettings.lists[listName];
        if (listSettings) {
          const validatedPageSize = validateNumberInRange(
            pageSize,
            MIN_PAGE_SIZE,
            listSettings.maxPageSize,
            listSettings.pageSize
          );
          set(state => ({
            paginationSettings: {
              ...state.paginationSettings,
              lists: {
                ...state.paginationSettings.lists,
                [listName]: { ...listSettings, pageSize: validatedPageSize }
              }
            }
          }));
        }
      },

      setTagsSettings: payload =>
        set(state => ({
          tagsSettings: {
            ...state.tagsSettings,
            ...payload,
            styling: state.tagsSettings.styling
              ? { ...state.tagsSettings.styling, ...payload.styling }
              : defaultTagStyling()
          }
        })),

      setTagVariant: payload =>
        set(state => ({ tagsSettings: { ...state.tagsSettings, variant: payload } })),

      setTagColorMode: payload =>
        set(state => ({ tagsSettings: { ...state.tagsSettings, colorMode: payload } })),

      setTagStyling: payload =>
        set(state => ({
          tagsSettings: {
            ...state.tagsSettings,
            styling: {
              ...(state.tagsSettings.styling ?? defaultTagStyling()),
              ...payload
            }
          }
        })),

      setTagUniformColor: payload =>
        set(state => ({
          tagsSettings: {
            ...state.tagsSettings,
            styling: {
              ...(state.tagsSettings.styling ?? defaultTagStyling()),
              uniformColor: validateColorValue(
                payload,
                state.tagsSettings.styling?.uniformColor ?? DEFAULT_UNIFORM_COLOR
              )
            }
          }
        })),

      setTagBorderSize: payload =>
        set(state => ({
          tagsSettings: {
            ...state.tagsSettings,
            styling: {
              ...(state.tagsSettings.styling ?? defaultTagStyling()),
              borderSize: validateNumberInRange(
                payload,
                MIN_BORDER_SIZE,
                MAX_BORDER_SIZE,
                state.tagsSettings.styling?.borderSize ?? DEFAULT_BORDER_SIZE
              )
            }
          }
        })),

      setTagFontSize: payload =>
        set(state => ({
          tagsSettings: {
            ...state.tagsSettings,
            styling: {
              ...(state.tagsSettings.styling ?? defaultTagStyling()),
              fontSize: validateNumberInRange(
                payload,
                MIN_FONT_SIZE,
                MAX_FONT_SIZE,
                state.tagsSettings.styling?.fontSize ?? DEFAULT_FONT_SIZE
              )
            }
          }
        })),

      resetTagsSettings: () => set({ tagsSettings: loadTagsSettingsFromStorage() }),

      setDateTimePickerSettings: payload =>
        set(state => ({
          dateTimePickerSettings: {
            ...(state.dateTimePickerSettings ?? defaultDateTimeSettings()),
            ...payload
          }
        })),

      setDateTimeDateFormat: payload =>
        set(state => ({
          dateTimePickerSettings: {
            ...(state.dateTimePickerSettings ?? defaultDateTimeSettings()),
            dateFormat: payload
          }
        })),

      setDateTimeTimeFormat: payload =>
        set(state => ({
          dateTimePickerSettings: {
            ...(state.dateTimePickerSettings ?? defaultDateTimeSettings()),
            timeFormat: payload
          }
        })),

      setDateTimeDisplayMode: payload =>
        set(state => ({
          dateTimePickerSettings: {
            ...(state.dateTimePickerSettings ?? defaultDateTimeSettings()),
            displayMode: payload
          }
        })),

      setDateTimeTimeStep: payload =>
        set(state => {
          const current = state.dateTimePickerSettings ?? defaultDateTimeSettings();
          return {
            dateTimePickerSettings: {
              ...current,
              timeStep: validateNumberInRange(payload, 60, 3600, current.timeStep)
            }
          };
        }),

      setDateTimeUse24HourFormat: payload =>
        set(state => ({
          dateTimePickerSettings: {
            ...(state.dateTimePickerSettings ?? defaultDateTimeSettings()),
            timeFormat: payload ? TimeFormat.H24 : TimeFormat.H12
          }
        })),

      setDateTimeMondayFirst: payload =>
        set(state => ({
          dateTimePickerSettings: {
            ...(state.dateTimePickerSettings ?? defaultDateTimeSettings()),
            mondayFirst: payload
          }
        })),

      resetDateTimePickerSettings: () =>
        set({ dateTimePickerSettings: loadDateTimePickerSettingsFromStorage() })
    }),
    {
      name: 'config-ui',
      partialize: state => ({
        paginationSettings: state.paginationSettings,
        tagsSettings: state.tagsSettings,
        dateTimePickerSettings: state.dateTimePickerSettings
      })
    }
  )
);
